import { useCallback, useEffect, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'
import { getCookie, setCookie, deleteCookie } from 'cookies-next'
import { jwtDecode } from 'jwt-decode'

import { useRouterParams } from './useRouterParams'

import { QR_CODE_ROTATION_PARAM } from '@/modules/projects/constants'
import { CHECK_ROTATING_QR_CODE_ACCESS } from '@/modules/webInduction/graphQL'

import {
  CheckProjectCodeUiMutation,
  CheckProjectCodeUiMutationVariables,
} from 'types/api.graphql'

const COOKIE_KEY = 'buildpass-qr-code-rotation-access'

const getCookieName = (projectId: string) => `${COOKIE_KEY}-${projectId}`

const extractCodeFromUrl = (url: string) => {
  if (!url) return null
  return new URL(url).searchParams.get(QR_CODE_ROTATION_PARAM)
}

const hasAccessViaCookie = (projectId: string) => {
  const cookie = getCookie(getCookieName(projectId))

  if (!cookie) return false

  try {
    const { exp, sub } = jwtDecode(cookie)

    // if the token is expired, then they likely tampered with the cookie expiry, delete the cookie
    if (new Date(exp * 1000) < new Date()) {
      deleteCookie(getCookieName(projectId))
      return false
    }

    // if the token is for a different project, delete the cookie
    if (sub !== projectId) {
      deleteCookie(getCookieName(projectId))

      return false
    }

    return true
  } catch {
    // if the cookie is invalid, delete it
    deleteCookie(getCookieName(projectId))
    return false
  }
}

type Params = {
  projectId: string
  [QR_CODE_ROTATION_PARAM]: number
}

export const useRotatingQrCodeAccess = () => {
  const grantedAccess = useRef(false)

  const { projectId, [QR_CODE_ROTATION_PARAM]: code } =
    useRouterParams<Params>()

  const [hasAccess, setHasAccess] = useState(hasAccessViaCookie(projectId))

  const [checkCode] = useMutation<
    CheckProjectCodeUiMutation,
    CheckProjectCodeUiMutationVariables
  >(CHECK_ROTATING_QR_CODE_ACCESS)

  const grantAccess = useCallback(
    async (projectId: string, code: string | number) => {
      const { data } = await checkCode({
        variables: { projectId, code: code.toString() },
      })

      const typename = data?.checkProjectCode_public?.__typename

      if (typename === 'CodeForProjectCheck_Failure') {
        setHasAccess(false)
        return false
      }

      if (typename === 'CodeForProjectCheck_Success') {
        const expiry = data.checkProjectCode_public.expiresAt
        const token = data.checkProjectCode_public.token

        setCookie(getCookieName(projectId), token, {
          expires: new Date(expiry),
          path: '/',
        })

        setHasAccess(true)
        return true
      }
    },
    [checkCode]
  )

  // attempt to grant access if we have a code on page load
  useEffect(() => {
    if (!hasAccess && projectId && code) {
      if (grantedAccess.current) return

      grantedAccess.current = true
      grantAccess(projectId, code)
    }
  }, [hasAccess, grantAccess, projectId, code])

  return { hasAccess, grantAccess, extractCodeFromUrl }
}
